import React from "react";

import { assert, definedMap } from "@glideapps/ts-necessities";
import {
    asArrayValue,
    asBoolean,
    asDate,
    asObjectValue,
    asString,
    isLoadedDefined,
    loadedDefinedMap,
} from "common/components/component-helpers";
import { areUniqueVPsOtherwiseEqual } from "common/components/data";
import type {
    AppEnvironment,
    AppOther,
    ForceValueProducer,
    RenderEnvironment,
    Value,
    ValueProducer,
} from "common/components/types";
import { ActionSource, asUI, LOADING } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { defined, mapFilterUndefined } from "common/Support";
import { getNowTimeZoneAgnostic } from "date-time/support";
import { useForce } from "hooks/use-force";
import { useNetworkStatus } from "hooks/use-network-status";
import type { OnClickHandlerWithCustomContext } from "hooks/use-on-click-from-action";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import EmptyWrapper from "webapp/ui/views/empty-wrapper/empty-wrapper";
import SwipeStack from "webapp/ui/views/swipe-stack/swipe-stack";
import type { PotentialValue } from "common/components/computation-types";

interface Item {
    readonly swipedTimestamp: Date;
    readonly swipedTimestampVP: ValueProducer | undefined;
    readonly key: string;
    readonly isEnabled: boolean;
    readonly data: ValueProducer;
}

function deconstructItems(list: Value, force: ForceValueProducer): readonly Item[] {
    return mapFilterUndefined(asArrayValue(list), vp => {
        const itemValue = force(vp);
        if (!isLoadedDefined(itemValue)) return undefined;
        const {
            data: dataVP,
            swipedTimestamp: swipedTimestampVP,
            key: keyVP,
            isEnabled: isEnabledVP,
        } = asObjectValue(itemValue);
        const swipedTimestampValue = definedMap(swipedTimestampVP, force);
        const swipedTimestamp = asDate(swipedTimestampValue) ?? new Date(2018, 8, 5);
        const keyValue = force(keyVP);
        if (swipedTimestampValue === LOADING || keyValue === LOADING) return undefined;
        const isEnabledValue = force(isEnabledVP);
        const isEnabled = isEnabledValue === LOADING ? false : definedMap(isEnabledValue, asBoolean) ?? true;
        return { swipedTimestamp, swipedTimestampVP, key: asString(keyValue), isEnabled, data: dataVP };
    });
}

const SwipeStackWithFirstSecond: React.FunctionComponent<{
    first: Item;
    second: Item | undefined;
    onClickLeft: OnClickHandlerWithCustomContext;
    onClickRight: OnClickHandlerWithCustomContext;
    isEnabled: boolean;
    contents: PotentialValue<AppOther, AppEnvironment>;
    env: RenderEnvironment;
    force: ReturnType<typeof useForce>[0];
    rowToSetVP: ValueProducer | undefined;
}> = p => {
    // We re-render far more often than we'd like to re-render.
    // Mainly because render loop bugs are very easy to trigger.
    // Hence, everything is memoized even if it feels unnecessary.
    //
    // -Dan
    const { first, second, onClickLeft, onClickRight, isEnabled, contents, env, force, rowToSetVP } = p;

    assert(contents !== LOADING);

    const frontContents = asUI(contents).render(first.data, env);
    const rearContents =
        second !== undefined
            ? asUI(contents).render(second.data, env.with({ omitOverlays: true }))
            : // Even if we're rendering the first again, we have to render it again without
              // overlays, or else we'll get multiple copies of the overlays.
              asUI(contents).render(first.data, env.with({ omitOverlays: true }));

    const swipe = React.useCallback(
        (handler: OnClickHandlerWithCustomContext) => {
            defined(first.swipedTimestampVP).set(getNowTimeZoneAgnostic());
            if (handler !== undefined) {
                void handler(first.data, undefined);
            }
        },
        [first]
    );

    const onLeft = React.useCallback(() => swipe(onClickLeft), [swipe, onClickLeft]);
    const onRight = React.useCallback(() => swipe(onClickRight), [swipe, onClickRight]);

    if (rowToSetVP !== undefined) {
        const firstDataValue = force(first.data);
        // FIXME: If the row is loading, we'll display it as not present instead.
        // We could do a shitty hack and set it to the string "Loading" or something,
        // or instead use a resettable forwarding value producer or something.

        // We have to make sure that we aren't spuriously setting the rowToSetVP
        // to somthing it already has. If we do that, we trigger an unnecessary
        // dirtying and an entire render loop.
        // FIXME: Not dirtying when set to the same value should probably be standard
        // behavior.
        if (!areUniqueVPsOtherwiseEqual(rowToSetVP.current, firstDataValue)) {
            rowToSetVP.set(firstDataValue === LOADING ? undefined : firstDataValue);
        }
    }

    return (
        <SwipeStack
            frontKey={first.key}
            // If we're using `first` as another rear element, we need to make sure
            // that they keys are still unique, otherwise we'll end up with weird
            // side effects. One such example of weird side effects in the wild:
            // multiple copies of the floating action bubble (which happened before
            // we fixed the omitOverlay problem).
            rearKey={second?.key ?? `${first.key}-rear`}
            onLeft={onLeft}
            onRight={onRight}
            frontContents={frontContents}
            rearContents={rearContents}
            isEnabled={isEnabled}
        />
    );
};

export const TinderMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[listValue, contents, onLeftValue, onRightValue, emptyMessageValue], [, , , , , rowToSetVP]] =
        useForceValueProducers(getValueProducers, force, ctx);
    const { onClickWithCustomContext: onClickLeft } = useOnClickFromAction(onLeftValue, ActionSource.Tinder, ctx, env);
    const { onClickWithCustomContext: onClickRight } = useOnClickFromAction(
        onRightValue,
        ActionSource.Tinder,
        ctx,
        env
    );
    const onLine = useNetworkStatus();
    if (listValue === LOADING || contents === LOADING) return env.renderProgress();
    if (onLeftValue === LOADING || onRightValue === LOADING) return env.renderProgress();
    if (listValue === undefined || contents === undefined) return null;

    const items = deconstructItems(listValue, force);
    if (items.length === 0) {
        if (rowToSetVP !== undefined) {
            rowToSetVP.set(undefined);
        }

        const emptyMessage = loadedDefinedMap(emptyMessageValue, asString);
        return <EmptyWrapper>{emptyMessage}</EmptyWrapper>;
    }

    let topTwo: [Item | undefined, Item | undefined] = [undefined, undefined];

    for (const i of items) {
        if (topTwo[0] === undefined || i.swipedTimestamp < topTwo[0].swipedTimestamp) {
            topTwo = [i, topTwo[0]];
        } else if (topTwo[1] === undefined || i.swipedTimestamp < topTwo[1].swipedTimestamp) {
            topTwo = [topTwo[0], i];
        }
    }

    const [maybeFirst, second] = topTwo;
    const first = defined(maybeFirst);
    const isEnabled = onLine && first.isEnabled && first.swipedTimestampVP !== undefined;

    return (
        <SwipeStackWithFirstSecond
            first={first}
            second={second}
            onClickLeft={onClickLeft}
            onClickRight={onClickRight}
            contents={contents}
            env={env}
            isEnabled={isEnabled}
            force={force}
            rowToSetVP={rowToSetVP}
        />
    );
};
